import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const POLLING_INTERVAL = 15000; // Poll every 15 seconds

function SalesListPage() {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const lastFetchedId = useRef(0);

  const fetchSales = useCallback(async () => {
    try {
      const API_BASE_URL =
        process.env.REACT_APP_API_BASE_URL || "https://installmentzone.com/api";
      const apiUrl = `${API_BASE_URL}/sales/sales.php?lastFetchedId=${lastFetchedId.current}`;

      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      if (result.success && Array.isArray(result.data)) {
        const newSales = result.data;

        if (newSales.length > 0) {
          lastFetchedId.current = Math.max(
            ...newSales.map((sale) => sale.id),
            lastFetchedId.current
          );

          if (newSales.length === 1) {
            toast.info(`New sale added: ${newSales[0].customer_name}`);
          } else {
            toast.info(`${newSales.length} new sales added.`);
          }

          setSales((prevSales) => [...newSales, ...prevSales]);
        }
      } else {
        throw new Error(result.error || "Failed to fetch sales or invalid data format.");
      }
    } catch (err) {
      setError(err.message || "An error occurred while fetching sales.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSales();

    const intervalId = setInterval(() => {
      fetchSales();
    }, POLLING_INTERVAL);

    return () => clearInterval(intervalId);
  }, [fetchSales]);

  if (loading) {
    return <p style={{ color: "#fff", textAlign: "center" }}>Loading sales...</p>;
  }

  if (error) {
    return (
      <div
        style={{
          padding: "20px",
          margin: "20px auto",
          background: "rgba(255, 255, 255, 0.1)",
          border: "1px solid red",
          borderRadius: "10px",
          maxWidth: "800px",
          color: "#fff",
          textAlign: "center",
        }}
      >
        <h3 style={{ color: "#F87171" }}>Error Fetching Sales</h3>
        <p>{error}</p>
        <button
          onClick={fetchSales}
          style={{
            padding: "10px 20px",
            marginTop: "10px",
            background: "#1E3A8A",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "500",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        background: "linear-gradient(135deg, #1E3A8A, #3B82F6, #60A5FA)",
        fontFamily: "Segoe UI, sans-serif",
        color: "#fff",
        overflowY: "auto", // ✅ stable scroll
        WebkitOverflowScrolling: "touch",
        scrollBehavior: "smooth",
      }}
    >
      <ToastContainer />
      <div
        style={{
          background: "rgba(255, 255, 255, 0.15)",
          padding: "30px",
          borderRadius: "15px",
          maxWidth: "1000px",
          margin: "0 auto",
          boxShadow: "0 6px 15px rgba(0, 0, 0, 0.2)",
          backdropFilter: "none", // ✅ removes shaking
        }}
      >
        <h2
          style={{
            textAlign: "center",
            fontSize: "28px",
            fontWeight: "600",
            marginBottom: "25px",
          }}
        >
          Sales Management
        </h2>

        {sales.length === 0 ? (
          <p style={{ textAlign: "center" }}>No sales found.</p>
        ) : (
          <div style={{ overflowX: "auto", willChange: "transform" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                background: "rgba(255, 255, 255, 0.9)",
                borderRadius: "10px",
                overflow: "hidden",
                color: "#1E293B",
                tableLayout: "fixed",
              }}
            >
              <thead>
                <tr style={{ background: "#1E3A8A", color: "#fff" }}>
                  <th style={{ padding: "14px", textAlign: "left" }}>ID</th>
                  <th style={{ padding: "14px", textAlign: "left" }}>Customer Name</th>
                  <th style={{ padding: "14px", textAlign: "left" }}>Device Name</th>
                  <th style={{ padding: "14px", textAlign: "left" }}>IMEI</th>
                  <th style={{ padding: "14px", textAlign: "left" }}>Sale Price</th>
                  <th style={{ padding: "14px", textAlign: "left" }}>Sale Date</th>
                  <th style={{ padding: "14px", textAlign: "left" }}>Payment Status</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((sale, index) => (
                  <tr
                    key={sale.id}
                    style={{
                      background: index % 2 === 0 ? "#F8FAFC" : "#FFFFFF",
                      borderBottom: "1px solid #E5E7EB",
                      transition: "background 0.2s ease",
                    }}
                  >
                    <td style={{ padding: "12px" }}>{sale.id}</td>
                    <td style={{ padding: "12px" }}>{sale.customer_name || "N/A"}</td>
                    <td style={{ padding: "12px" }}>{sale.sold_device_name || "N/A"}</td>
                    <td style={{ padding: "12px" }}>{sale.sold_device_imei || "N/A"}</td>
                    <td style={{ padding: "12px", color: "#0F766E", fontWeight: "500" }}>
                      {sale.sale_price
                        ? `GH₵${Number(sale.sale_price).toLocaleString("en-GH", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                        : "N/A"}
                    </td>
                    <td style={{ padding: "12px" }}>
                      {sale.sale_date ? new Date(sale.sale_date).toLocaleString() : "N/A"}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        fontWeight: "600",
                        color:
                          sale.payment_status?.toLowerCase() === "paid"
                            ? "#16A34A"
                            : sale.payment_status?.toLowerCase() === "pending"
                            ? "#CA8A04"
                            : "#DC2626",
                      }}
                    >
                      {sale.payment_status || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default SalesListPage;
